import React from 'react';
import LazyTable from 'app/component/lazy-table';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { formatDateFromInstant, formatFromLocalDate } from 'app/shared/util/date-utils';
import { FilterMatchMode } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import DateTextInput from 'app/component/date-text-input';

const NfaCardTable: React.FC = () => {
  const isAdminOrAssistant = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]),
  );

  const isProcessedIcon = processed => {
    return (
      <span>
        {processed ? (
          <i className="pi pi-check" style={{ color: 'green', fontWeight: 'bold' }}></i>
        ) : (
          <i className="pi pi-times" style={{ color: 'red', fontWeight: 'bold' }}></i>
        )}
      </span>
    );
  };

  const formatKlient = clients => {
    if (clients?.length > 0) {
      if (clients[0].firstName) {
        if (clients[0].lastName) {
          return clients[0].firstName + ' ' + clients[0].lastName;
        }
        return clients[0].firstName;
      }

      return clients[0].businessName;
    }

    return '';
  };

  const formatPoradca = agent => {
    if (agent.firstName) {
      if (agent.lastName) {
        return agent.firstName + ' ' + agent.lastName;
      }
      return agent.firstName;
    }

    return agent.name;
  };

  const booleanFilterTemplate = options => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Všetko', value: null },
          { label: 'Áno', value: true },
          { label: 'Nie', value: false },
        ]}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Vyber"
        className="p-column-filter"
        showClear
      />
    );
  };

  const dateRangeFilterTemplate = options => {
    const value = options.value || [null, null];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
        <DateTextInput
          value={value[0] || ''}
          onChange={val => options.filterApplyCallback([val || null, value[1]])}
        />
        <DateTextInput
          value={value[1] || ''}
          onChange={val => options.filterApplyCallback([value[0], val || null])}
        />
      </div>
    );
  };

  return LazyTable({
    initialSortField: 'createdDate',
    initialSortDirection: 'desc',
    createUrl: '/create-nfa-card',
    labelForCreateEntity: isAdminOrAssistant ? 'Vytvoriť NFA Kartu' : null,
    serverUrl: '/api/nfa-cards/search',
    editActionAllowed: isAdminOrAssistant,
    infoActionAllowed: !isAdminOrAssistant,
    deleteActionAllowed: isAdminOrAssistant,
    globalFilterFields: ['agentName', 'clientName'],
    searchAllowed: true,
    columns: [
      { field: 'createdDate', header: 'Vytvorené', sortable: true, formatter: formatDateFromInstant },
      { field: 'agent', header: 'Poradca', sortable: true, filter: true, filterField: 'agentName', formatter: formatPoradca },
      { field: 'clients', header: 'Klient', sortable: true, filter: true, filterField: 'clientName', formatter: formatKlient },
      { field: 'prevodZmluv', header: 'Prevod zmlúv', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemDruzstvo', header: 'Záujem Družstvo NFA Invest', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemInvestments', header: 'Záujem NFA Investments', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemLegal', header: 'Záujem NFA Legal Counseling', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemAccounting', header: 'Záujem NFA Accounting Services', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemRealEstate', header: 'Záujem NFA Real Estate', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'zaujemSpolupraca', header: 'Záujem spolupráca', sortable: true, filter: true, filterElement: booleanFilterTemplate, formatter: isProcessedIcon },
      { field: 'prveStretnutie', header: 'Prvé stretnutie', sortable: true, filter: true, filterElement: dateRangeFilterTemplate, formatter: formatFromLocalDate },
      { field: 'druheStretnutie', header: 'Druhé stretnutie', sortable: true, filter: true, filterElement: dateRangeFilterTemplate, formatter: formatFromLocalDate },
    ],
    filters: {
      agentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      clientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      prevodZmluv: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemDruzstvo: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemInvestments: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemLegal: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemAccounting: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemRealEstate: { value: null, matchMode: FilterMatchMode.EQUALS },
      zaujemSpolupraca: { value: null, matchMode: FilterMatchMode.EQUALS },
      prveStretnutie: { value: null, matchMode: FilterMatchMode.BETWEEN },
      druheStretnutie: { value: null, matchMode: FilterMatchMode.BETWEEN },
    },
  });
};

export default NfaCardTable;
